import { createFileRoute } from '@tanstack/react-router'
import { ComparisonPage, comparisonScripts, type ComparisonData } from '@/components/comparison-page'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'

export const Route = createFileRoute('/_site/compare/pulsetic')({
  component: () => <ComparisonPage data={data} />,
  head: () => ({
    meta: [
      { title: 'Pulsetic Alternative — Uptrack Uptime Monitoring' },
      { name: 'description', content: "Uptrack vs Pulsetic: Pulsetic has a free plan and 30s checks on its Team plan. Uptrack checks every 30s on every plan, free or paid from $3/mo." },
      { property: 'og:title', content: 'Pulsetic Alternative — Uptrack' },
      { property: 'og:description', content: "Compare Uptrack and Pulsetic on check intervals, status pages, regions, seats, and pricing." },
      { property: 'og:image', content: 'https://uptrack.app/og/compare-pulsetic.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/compare/pulsetic' },
    ],
    scripts: comparisonScripts('pulsetic', "Pulsetic"),
  }),
})

const data: ComparisonData = {
  competitor: "Pulsetic",
  tagline: "Pulsetic focuses on status pages and uptime monitoring, with a free plan of 10 monitors at 5-minute checks. Uptrack checks every 30 seconds on every plan, from $3/mo.",
  heroDescription: "These facts come from Pulsetic's public pricing and docs pages as of October 2026. Plans change often, so check their site before you decide.",

  features: [
    { feature: "Free plan", uptrack: "5 monitors at 30 s from Nuremberg", competitor: "10 monitors at 5 min, 3 status pages" },
    { feature: "Interval on cheapest paid plan", uptrack: "30 sec (Pro)", competitor: "60 sec (Solo)", uptrackWins: true },
    { feature: "30-second checks", uptrack: "Every plan", competitor: "Team plan and up", uptrackWins: true },
    { feature: "Team members", uptrack: "Included: 3 on Pro, 5 on Team, 15 on Business", competitor: "$8 per teammate per month", uptrackWins: true },
    { feature: "Check regions", uptrack: MONITOR_LOCATIONS.compare.cell, competitor: "3, 5, or 15, by plan", uptrackWins: MONITOR_LOCATIONS.compare.wins },
    { feature: "Status pages", uptrack: "5 on Pro, unlimited on Team", competitor: "Unlimited on paid plans" },
    { feature: "Status page custom domain", uptrack: "Team and Business", competitor: "All plans, including free" },
  ],

  painPoints: [
    {
      title: "30-second checks start on Team",
      description: "Pulsetic's free plan checks every 5 minutes and its Solo plan every 60 seconds. 30-second checks start on the Team plan. Every Uptrack plan checks every 30 seconds.",
    },
    {
      title: "Teammates cost extra",
      description: "Pulsetic charges $8 per teammate per month, and $2 per notify-only seat. Uptrack plans include seats: 3 on Pro, 5 on Team, 15 on Business.",
    },
    {
      title: "Browser flows on the entry plan",
      description: "Uptrack Pro includes 5 Playwright browser monitors for $3/mo. Each run follows a flow such as login or checkout and saves a screenshot at each step, so you see which step broke.",
    },
  ],

  differentiators: [
    "30-second checks on every plan: Pro is $3/mo for 50 monitors, Team is $20/mo for 200, Business is $79/mo for 1,000.",
    "Alert confirmation: Uptrack opens an incident only after 2-5 consecutive failed checks, and several check locations must confirm. One DNS timeout or CDN blip does not page you.",
    "Browser checks: Pro includes 5 Playwright monitors that run flows like login or checkout and save a screenshot at each step.",
    "Escalation policies on Team and Business route an unacknowledged alert to the next person after a delay you set.",
    "REST API and an MCP server, so you can manage monitors from scripts or AI assistants such as Claude.",
    "2 years of check history on Pro and Team, 5 years on Business.",
  ],

  notForYou: [
    "If status page design is your top priority, Pulsetic's status pages and visual editor are a strong fit.",
    "If you want a custom domain on a free status page, Pulsetic includes custom domains on all plans. Uptrack includes them on Team and Business.",
    "If you need SMS, call, or WhatsApp alerts, Pulsetic sells them per message.",
    "If you need more than 5 free monitors, Pulsetic offers 10 monitors at 5-minute checks. Uptrack's free plan is 5 monitors at 30 seconds from one location.",
  ],
}
